<script setup lang="ts">
// Global host for the Ask command palette. Owns:
//   • the Cmd/Ctrl+K + Esc keybindings (registered once at the layout level)
//   • the auth/route gate (any verified user — matching the launcher — never on
//     /ask itself)
//
// The palette component itself (AskCommandPalette) is rendered conditionally
// here so it never mounts unless there's a verified user on a page that should
// expose the surface. The palette is also Teleported to <body> inside its
// template, so this host can sit anywhere in the tree.

import { useAskPalette } from '~/composables/useAskPalette';

const route = useRoute();
const { isOpenRef, toggle, close } = useAskPalette();

// Gated to fully verified agents (token + phoneVerified). Unverified users
// neither see the launcher nor get the Cmd+K shortcut — Ask is off-limits
// until verification completes.
const accessLevel = useAccessLevel();
const isVerified = computed(() => accessLevel.value === 'verified');
const isOnAskPage = computed(() => {
  const p = route.path || '';
  return p === '/ask' || p.startsWith('/ask/');
});
// Match the launcher's gate exactly (any verified user — including free-plan
// masterclass leads who now have Ask access), so clicking the "AI" button (or
// Cmd+K) always opens the palette. Disabled only on /ask itself, which renders
// its own full-page chat.
const enabled = computed(() => isVerified.value && !isOnAskPage.value);

const onKeydown = (ev: KeyboardEvent) => {
  if (!enabled.value) return;

  // Cmd+K on Mac, Ctrl+K elsewhere. We listen for both unconditionally so the
  // shortcut works regardless of platform without relying on UA sniffing.
  const k = ev.key?.toLowerCase();
  if (k === 'k' && (ev.metaKey || ev.ctrlKey)) {
    ev.preventDefault();
    toggle();
    return;
  }

  if (k === 'escape' && isOpenRef.value) {
    ev.preventDefault();
    close();
  }
};

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});

// Belt-and-braces: if the user lands on /ask while the palette is open, close
// it so the page-level chat is the only visible surface.
watch(isOnAskPage, (onAsk) => {
  if (onAsk && isOpenRef.value) close();
});
</script>

<template>
  <ClientOnly>
    <AskCommandPalette v-if="enabled" />
  </ClientOnly>
</template>
